// utils/tagUtils.js

export const TAG_PATH_SEP = " / ";

/** Basic slugify for stable keys derived from fullPath. */
export function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Normalize + recompose a full path with canonical spacing/order. */
export function normalizeFullPath(path) {
  const segs = segmentsFromPath(path);
  return segs.join(TAG_PATH_SEP);
}

/**
 * Split a cell value into *paths*.
 * - Comma is the primary multi-path separator (also supports | and ;)
 * - Supports escaping separators with backslash: "\," "\|" "\;"
 * - Whitespace around separators is ignored
 */
export function splitTagPathsCell(cellValue) {
  if (cellValue == null) return [];
  const s = String(cellValue);
  const out = [];
  let buf = "";
  let i = 0;

  const isSep = (ch) => ch === "," || ch === "|" || ch === ";";

  while (i < s.length) {
    const ch = s[i];

    // Escape sequence for separators
    if (ch === "\\" && i + 1 < s.length) {
      const next = s[i + 1];
      if (isSep(next) || next === "\\") {
        buf += next; // keep literal separator or backslash
        i += 2;
        continue;
      }
    }

    // Unescaped separator → finalize current token
    if (isSep(ch)) {
      const token = buf.trim();
      if (token) out.push(token);
      buf = "";
      i++;
      while (i < s.length && (s[i] === " " || s[i] === "\t")) i++;
      continue;
    }

    buf += ch;
    i++;
  }

  const last = buf.trim();
  if (last) out.push(last);
  return out;
}

/**
 * Break a single path string into its hierarchical segments (parent → child).
 * Robust:
 *   - splits on ANY unescaped "/" (spaces optional around it)
 *   - "\/" keeps a literal slash in the segment
 *   - trims and removes empty segments
 */
export function segmentsFromPath(path) {
  const s = String(path || "").trim();
  if (!s) return [];

  const segs = [];
  let buf = "";
  let i = 0;

  while (i < s.length) {
    const ch = s[i];

    // handle escapes
    if (ch === "\\" && i + 1 < s.length) {
      const next = s[i + 1];
      // keep escaped slash or backslash literally
      if (next === "/" || next === "\\") {
        buf += next;
        i += 2;
        continue;
      }
      buf += ch;
      i++;
      continue;
    }

    // treat any "/" as a separator; allow arbitrary surrounding spaces
    if (ch === "/") {
      const token = buf.trim();
      if (token) segs.push(token);
      buf = "";
      i++;
      while (i < s.length && s[i] === " ") i++;
      continue;
    }

    buf += ch;
    i++;
  }

  const last = buf.trim();
  if (last) segs.push(last);

  return segs;
}

/**
 * Build a flat index:
 *   SEGMENT (e.g., "Dates") -> [transaction IDs]
 * Behavior:
 *   - Each segment along a path indexes the transaction (so searching by parent works).
 *   - IDs per segment are de-duplicated and preserve first-seen order.
 *   - Segment list preserves first-seen order (no alphabetical sorting).
 */
export function buildSegmentIndexFromRows(rows, idColIndex, tagsColIndex) {
  const index = Object.create(null); // segment -> array of ids (with potential dups before dedupe)
  const segmentOrder = [];           // first-seen order of segments
  const segmentSeen = new Set();     // to track first-seen order

  for (const row of rows || []) {
    const rawId = row?.[idColIndex];
    const id = String(rawId ?? "").trim();
    if (!id) continue;

    const paths = splitTagPathsCell(row?.[tagsColIndex]);
    for (const p of paths) {
      const segments = segmentsFromPath(p); // LEFT->RIGHT
      for (const seg of segments) {
        if (!index[seg]) {
          index[seg] = [];
          if (!segmentSeen.has(seg)) {
            segmentSeen.add(seg);
            segmentOrder.push(seg);
          }
        }
        index[seg].push(id); // keep encounter order
      }
    }
  }

  // Deduplicate IDs per segment while preserving encounter order
  for (const seg of Object.keys(index)) {
    const ids = index[seg];
    const deduped = [];
    const seenIds = new Set();
    for (const tid of ids) {
      if (!seenIds.has(tid)) {
        seenIds.add(tid);
        deduped.push(tid);
      }
    }
    index[seg] = deduped;
  }

  const uniqueSegments = segmentOrder.slice(); // preserve first-seen order
  return { index, uniqueSegments };
}

/**
 * Build the tag structures used by the UI:
 *   - nodes: flat array of unique nodes in *first-seen* order
 *            where `name` is the segment label (no commas, not reversed),
 *            and `fullPath` uses TAG_PATH_SEP in parent→child order.
 *   - tree:  hierarchical structure with children arrays preserving first-seen order.
 */
export function buildTags(paths) {
  const root = { children: [], _childMap: new Map() };

  const uniqueNodes = [];
  const seenFullPath = new Set();

  for (const raw of paths || []) {
    const path = normalizeFullPath(raw);
    const segs = segmentsFromPath(path); // ["A", "B"] — LEFT->RIGHT
    let parent = root;
    const ancestry = [];

    for (const seg of segs) {
      ancestry.push(seg);
      const fullPath = ancestry.join(TAG_PATH_SEP);

      let child = parent._childMap.get(seg);
      if (!child) {
        child = {
          name: seg,
          fullPath,
          slug: slugify(fullPath),
          children: [],
          _childMap: new Map(),
        };
        parent._childMap.set(seg, child);
        parent.children.push(child); // preserve encounter order
      }

      if (!seenFullPath.has(fullPath)) {
        seenFullPath.add(fullPath);
        uniqueNodes.push({ name: child.name, fullPath: child.fullPath, slug: child.slug });
      }

      parent = child;
    }
  }

  (function stripMaps(node) {
    delete node._childMap;
    for (const c of node.children) stripMaps(c);
  })(root);

  return { nodes: uniqueNodes, tree: root.children };
}

/** Optional: build a prefix index for path-aware searching. */
export function buildPathPrefixIndex(paths) {
  const idx = new Map(); // "A", "A / B", "A / B / C" -> Set(fullPath)
  for (const raw of paths || []) {
    const segs = segmentsFromPath(raw);
    const prefixes = [];
    for (let i = 0; i < segs.length; i++) {
      prefixes.push(segs[i]);
      const key = prefixes.join(TAG_PATH_SEP);
      if (!idx.has(key)) idx.set(key, new Set());
      idx.get(key).add(prefixes.join(TAG_PATH_SEP));
    }
  }
  return idx;
}

/**
 * Shape a tag doc into the canonical write format:
 *  - normalizes fullPath
 *  - sets name (leaf), slug
 *  - sets parentPath, parentSlug
 *  - builds ancestors[], depth
 */
export function shapeTagForWrite(doc) {
  const rawFull = doc?.fullPath || doc?.name || "";
  const fullPath = normalizeFullPath(rawFull);
  if (!fullPath) return null;

  const segs = segmentsFromPath(fullPath);
  const name = segs[segs.length - 1] || fullPath;
  const parentSegs = segs.slice(0, -1);
  const parentPath = parentSegs.join(TAG_PATH_SEP);
  const ancestors = parentSegs.map((_, i) => {
    const ap = segs.slice(0, i + 1).join(TAG_PATH_SEP);
    return { name: segs[i], fullPath: ap, slug: slugify(ap) };
    // Example i=0 -> "Living"; i=1 -> "Living / Home 193 Westbank Tr"; etc.
  });

  return {
    name,
    fullPath,
    slug: slugify(fullPath),
    parentPath,
    parentSlug: parentPath ? slugify(parentPath) : "",
    ancestors,
    depth: segs.length,
    ...(doc?.source ? { source: doc.source } : {}),
  };
}

/**
 * Flatten a hierarchical tree (from ProcessedDataComponent) into nodes
 * with guaranteed canonical fullPath and slug, preserving encounter order.
 * Input shape expected: { name, children: [...] }
 */
export function nodesFromTree(tree) {
  const out = [];
  function walk(node, ancestryNames = []) {
    const nm = node?.name || "";
    if (!nm) return;
    const names = ancestryNames.concat(nm);
    const fp = names.join(TAG_PATH_SEP);
    out.push({
      name: nm,
      fullPath: fp,
      slug: slugify(fp),
    });
    const kids = Array.isArray(node?.children) ? node.children : [];
    for (const k of kids) walk(k, names);
  }
  const roots = Array.isArray(tree) ? tree : [];
  for (const r of roots) walk(r, []);
  return out;
}
